/**
 * LC-4b — the asset's lifecycle on its CRM page (asset-lifecycle design §8).
 *
 *   GET  /api/assets/{id}/lifecycle             → {events[], deactivated, final, can_reactivate,
 *                                                  deactivation, collection}
 *   POST /api/assets/{id}/lifecycle/reactivate  → {reason}   (service managers / admins; the server decides)
 *
 * Pure text here; the panel renders it. Every word comes from the server's event (labels included),
 * so the CRM never restates the rules.
 */

const pad = (n) => String(n).padStart(2, '0');

export function day(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

const who = (e) => (e && (e.actor_name || e.actor_username)) || '';
const device = (product, serial) => [product, serial].filter(Boolean).join(' ');

/** The old device: «לא פעיל · שודרג ל־<product> <serial> ב־<date> ע״י <technician> · <opportunity>». */
export function inactiveBanner(state) {
  const d = state && state.deactivation;
  if (!state || !state.deactivated || !d) return null;
  const verb = d.kind === 'upgrade' ? 'שודרג ל־'
    : d.kind === 'lab_replacement' ? 'הוחלף במעבדה במכשיר '
      : d.kind === 'loaner_converted' ? 'הומר למכשיר החלופי '
        : 'הוחלף במכשיר ';
  const parts = [`לא פעיל · ${verb}${device(d.new_product_name, d.new_serial) || 'מכשיר חדש'}`];
  if (d.occurred_at) parts[0] += ` ב־${day(d.occurred_at)}`;
  if (who(d)) parts[0] += ` ע״י ${who(d)}`;
  if (d.upgrade && d.upgrade.opportunity_name) parts.push(d.upgrade.opportunity_name);
  if (d.reason) parts.push(d.reason);
  if (state.final) parts.push('סופי — המכשיר המחליף הותקן');
  return parts.join(' · ');
}

/** «מצב בעת האיסוף: תקול — נזילה, רעשים · "<text>"». */
export function collectionLine(state) {
  const c = state && state.collection;
  if (!c || !c.condition) return null;
  const reasons = (c.fault_reasons || []).join(', ');
  let line = `מצב בעת האיסוף: ${c.condition_label || c.condition}`;
  if (reasons) line += ` — ${reasons}`;
  if (c.fault_text) line += ` · "${c.fault_text}"`;
  return line;
}

/** The new device: «מחליף את <old product> <old serial> (לא פעיל)», with the old asset's id for the link. */
export function replacesLine(events) {
  const e = [...(events || [])].reverse()
    .find((x) => x.role === 'new' && x.event === 'deactivated');
  if (!e) return null;
  return { text: `מחליף את ${device(e.old_product_name, e.old_serial) || 'המכשיר הקודם'} (לא פעיל)`,
           assetId: e.asset_id };
}

/** One row of «מחזור חיים של המכשיר». */
export function eventRow(e) {
  const bits = [];
  if (e.kind_label && e.event === 'deactivated') bits.push(e.kind_label);
  if (e.condition_label) bits.push(e.condition_label + ((e.fault_reasons || []).length ? `: ${e.fault_reasons.join(', ')}` : ''));
  if (e.event === 'deactivated' && e.role === 'old' && (e.new_serial || e.new_product_name)) {
    bits.push(`הוחלף ב־${device(e.new_product_name, e.new_serial)}`);
  }
  if (e.role === 'new' && e.event === 'deactivated') bits.push(`במקום ${device(e.old_product_name, e.old_serial)}`);
  if (e.reason) bits.push(e.reason);
  return { date: day(e.occurred_at), title: e.event_label || e.event, detail: bits.join(' · '),
           actor: who(e), text: e.fault_text || null };
}

export const REACTIVATE_REASON_MIN = 5;
